/**
 * Operating status of a device as the status-summary endpoint reports it (tri-server#1364),
 * and everything the portal derives from it for display: badge classes, marker colours on the
 * floor plan, the due state of the next check and a relative "12 min ago".
 *
 * One place for these so the floor plan, its legend, the action menu and the equipment table
 * cannot drift apart.
 */

export const ASSET_STATUSES = [
  "normal",
  "warning",
  "critical",
  "maintenance",
  "decommissioned",
  "noData",
] as const;

export type AssetStatus = (typeof ASSET_STATUSES)[number];

export function isAssetStatus(value: unknown): value is AssetStatus {
  return typeof value === "string" && (ASSET_STATUSES as readonly string[]).includes(value);
}

/**
 * Badge classes per status. Normal/warning/critical use the theme tokens (success, warning,
 * destructive); maintenance and decommissioned reuse the blue/purple the operations dashboard
 * already uses for those states.
 */
export const ASSET_STATUS_BADGE_CLASSES: Record<AssetStatus, string> = {
  normal: "border-transparent bg-success text-success-foreground",
  warning: "border-transparent bg-warning text-warning-foreground",
  critical: "border-transparent bg-destructive text-destructive-foreground",
  maintenance: "border-transparent bg-blue-700 text-white",
  decommissioned: "border-transparent bg-purple-700 text-white",
  noData: "border-transparent bg-muted text-muted-foreground",
};

/**
 * Marker colours on the floor plan canvas (paper.js needs concrete colours, not CSS tokens).
 * Green / amber / red / grey as asked for in #1364, blue and purple as on the dashboard.
 */
export const ASSET_STATUS_MARKER_COLORS: Record<AssetStatus, string> = {
  normal: "#15803d",
  warning: "#d97706",
  critical: "#dc2626",
  maintenance: "#1d4ed8",
  decommissioned: "#7e22ce",
  noData: "#6b7280",
};

// ---------------------------------------------------------------------------
// Next check (#1180) — same thresholds as the app's `dueState`
// ---------------------------------------------------------------------------

export type DueState = "overdue" | "dueSoon" | "ok";

/** A check is "due soon" this many days ahead of its date. */
export const DUE_SOON_DAYS = 30;

function toTime(value: unknown): number | undefined {
  if (value == null || value === "") return undefined;
  const time =
    value instanceof Date
      ? value.getTime()
      : typeof value === "number"
        ? value
        : new Date(String(value)).getTime();
  return Number.isNaN(time) ? undefined : time;
}

/** Overdue once the date has passed, due soon within {@link DUE_SOON_DAYS}, ok otherwise. */
export function dueState(due: unknown, now: number = Date.now()): DueState | null {
  const time = toTime(due);
  if (time === undefined) return null;
  const remaining = time - now;
  if (remaining < 0) return "overdue";
  if (remaining < DUE_SOON_DAYS * 24 * 60 * 60 * 1000) return "dueSoon";
  return "ok";
}

export const DUE_STATE_CLASSES: Record<DueState, string> = {
  overdue: "font-semibold text-destructive",
  dueSoon: "font-semibold text-warning",
  ok: "",
};

// ---------------------------------------------------------------------------
// Relative time — "vor 12 Min." / "12 min. ago"
// ---------------------------------------------------------------------------

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;
const WEEK = 7 * DAY;
const MONTH = 30 * DAY;
const YEAR = 365 * DAY;

/**
 * Constructing an `Intl` formatter is far more expensive than calling `format` on one, so the
 * formatters are built once per language and options. The cache is small and bounded: the
 * language comes from the UI settings, a handful of values.
 */
const FORMATTER_CACHE_LIMIT = 32;
const formatterCache = new Map<string, unknown>();

function cachedFormatter<T>(key: string, create: () => T): T {
  const hit = formatterCache.get(key);
  if (hit !== undefined) return hit as T;
  const created = create();
  if (formatterCache.size >= FORMATTER_CACHE_LIMIT) formatterCache.clear();
  formatterCache.set(key, created);
  return created;
}

/** The formatter for `language`, falling back to German when the language tag is invalid. */
function localeFormatter<T>(kind: string, language: string, create: (locale: string) => T): T {
  const locale = language || "de";
  try {
    return cachedFormatter(`${kind}|${locale}`, () => create(locale));
  } catch {
    return cachedFormatter(`${kind}|de`, () => create("de"));
  }
}

/**
 * Age of a timestamp as short relative text in the given language, via
 * `Intl.RelativeTimeFormat` ("vor 12 Min.", "gestern", "12 min. ago"). A timestamp in the
 * future (clock skew) counts as now. `undefined` without a usable date.
 */
export function formatRelativeTime(
  value: unknown,
  language: string,
  now: number = Date.now(),
): string | undefined {
  const time = toTime(value);
  if (time === undefined) return undefined;

  const elapsed = Math.max(0, now - time);
  const format = localeFormatter(
    "relative",
    language,
    (locale) => new Intl.RelativeTimeFormat(locale, { numeric: "auto", style: "short" }),
  );

  if (elapsed < MINUTE) return format.format(0, "second");
  if (elapsed < HOUR) return format.format(-Math.floor(elapsed / MINUTE), "minute");
  if (elapsed < DAY) return format.format(-Math.floor(elapsed / HOUR), "hour");
  if (elapsed < WEEK) return format.format(-Math.floor(elapsed / DAY), "day");
  if (elapsed < 2 * MONTH) return format.format(-Math.floor(elapsed / WEEK), "week");
  if (elapsed < YEAR) return format.format(-Math.floor(elapsed / MONTH), "month");
  return format.format(-Math.floor(elapsed / YEAR), "year");
}

// ---------------------------------------------------------------------------
// "Since" — "seit 3 Std." within a day, "seit 24.09., 07:24" beyond
// ---------------------------------------------------------------------------

function formatDuration(value: number, unit: "minute" | "hour", language: string): string {
  try {
    const locale = language || "de";
    return cachedFormatter(
      `unit|${unit}|${locale}`,
      () => new Intl.NumberFormat(locale, { style: "unit", unit, unitDisplay: "short" }),
    ).format(value);
  } catch {
    return `${value} ${unit === "hour" ? "h" : "min"}`;
  }
}

/** Short absolute date and time: "24.09., 07:24"; with the year once it is not this year's. */
export function formatDateTime(value: unknown, language: string, now: number = Date.now()): string | undefined {
  const time = toTime(value);
  if (time === undefined) return undefined;
  const date = new Date(time);
  const withYear = date.getFullYear() !== new Date(now).getFullYear();
  return localeFormatter(
    withYear ? "dateTimeYear" : "dateTime",
    language,
    (locale) =>
      new Intl.DateTimeFormat(locale, {
        day: "2-digit",
        month: "2-digit",
        ...(withYear ? { year: "numeric" } : {}),
        hour: "2-digit",
        minute: "2-digit",
      }),
  ).format(date);
}

/** Short absolute date: "17.09.2028". */
export function formatDate(value: unknown, language: string): string | undefined {
  const time = toTime(value);
  if (time === undefined) return undefined;
  return localeFormatter(
    "date",
    language,
    (locale) => new Intl.DateTimeFormat(locale, { day: "2-digit", month: "2-digit", year: "numeric" }),
  ).format(time);
}

/**
 * The point in time to put after "seit" / "since": a duration below one day ("3 Std.",
 * "12 Min."), the absolute date and time beyond ("24.09., 07:24"). Never a relative phrase —
 * "seit vor 6 Tagen" is not German.
 */
export function formatSince(value: unknown, language: string, now: number = Date.now()): string | undefined {
  const time = toTime(value);
  if (time === undefined) return undefined;
  const elapsed = Math.max(0, now - time);
  if (elapsed < HOUR) return formatDuration(Math.max(1, Math.floor(elapsed / MINUTE)), "minute", language);
  if (elapsed < DAY) return formatDuration(Math.floor(elapsed / HOUR), "hour", language);
  return formatDateTime(time, language, now);
}

// ---------------------------------------------------------------------------
// Readings — fixed decimals per quantity, locale aware ("15,4 bar", "3,53 V")
// ---------------------------------------------------------------------------

export type ReadingKind = "pressure" | "temperature" | "batteryVoltage" | "batteryStateOfCharge";

const READING_FORMAT: Record<ReadingKind, { digits: number; unit: string }> = {
  pressure: { digits: 1, unit: "bar" },
  temperature: { digits: 1, unit: "°C" },
  batteryVoltage: { digits: 2, unit: "V" },
  batteryStateOfCharge: { digits: 0, unit: "%" },
};

/** "15,4 bar"; `undefined` without a numeric value. */
export function formatReading(
  kind: ReadingKind,
  reading: { value?: unknown; unit?: string | null } | null | undefined,
  language: string,
): string | undefined {
  if (!reading || typeof reading.value !== "number" || !Number.isFinite(reading.value)) return undefined;
  const { digits, unit } = READING_FORMAT[kind];
  let number: string;
  try {
    const locale = language || "de";
    number = cachedFormatter(
      `reading|${digits}|${locale}`,
      () => new Intl.NumberFormat(locale, { minimumFractionDigits: digits, maximumFractionDigits: digits }),
    ).format(reading.value);
  } catch {
    number = reading.value.toFixed(digits);
  }
  return `${number} ${reading.unit || unit}`;
}

/** True when two timestamps lie more than an hour apart (or only one of them is known). */
export function differsByMoreThanAnHour(a: unknown, b: unknown): boolean {
  const first = toTime(a);
  const second = toTime(b);
  if (first === undefined) return false;
  if (second === undefined) return true;
  return Math.abs(first - second) > HOUR;
}

// ---------------------------------------------------------------------------
// Display state — offline vs. no data, stale data, severity, filter buckets (round 3)
// ---------------------------------------------------------------------------

/**
 * What the portal shows for a device. `offline` splits off the server's `noData`: a device
 * that did report once and has gone silent is offline; one that never delivered anything has
 * no data.
 */
export const ASSET_DISPLAY_STATUSES = [
  "critical",
  "warning",
  "offline",
  "maintenance",
  "noData",
  "normal",
  "decommissioned",
] as const;

export type AssetDisplayStatus = (typeof ASSET_DISPLAY_STATUSES)[number];

/** The fields of a status summary the display logic reads (tri-server#1364 contract). */
export interface StatusSummaryLike {
  status?: unknown;
  statusReason?: string | null;
  statusTimestamp?: string | null;
  lastUplinkAt?: string | null;
  /** Added by the server in round 3; optional until every server has it. */
  stale?: boolean | null;
  sendIntervalSeconds?: number | null;
  readings?: Record<string, { timestamp?: string | null } | undefined> | null;
}

/**
 * Last sign of life: the server's `lastUplinkAt`; without one, the newest reading's timestamp.
 */
export function lastSeenAt(summary: StatusSummaryLike): string | undefined {
  if (toTime(summary.lastUplinkAt) !== undefined) return String(summary.lastUplinkAt);
  let newest: { time: number; value: string } | undefined;
  for (const reading of Object.values(summary.readings ?? {})) {
    const time = toTime(reading?.timestamp);
    if (time !== undefined && (!newest || time > newest.time)) newest = { time, value: String(reading?.timestamp) };
  }
  return newest?.value;
}

/**
 * True when the data is not current. The server's `stale` flag when present; before it
 * shipped, only the one case the old contract names (`noData` / `noUplinkSince`).
 */
export function isStale(summary: StatusSummaryLike): boolean {
  if (typeof summary.stale === "boolean") return summary.stale;
  return summary.status === "noData" && summary.statusReason === "noUplinkSince";
}

export interface StatusView {
  /** Server status. */
  status: AssetStatus;
  /** What the badge says. */
  display: AssetDisplayStatus;
  /** Data is not current: values render muted, a hint says since when. */
  stale: boolean;
  /** Last sign of life (uplink or newest reading), for "Offline seit" / "Stand". */
  lastSeenAt?: string;
}

/**
 * Status, display status and staleness without the last sign of life, which needs a date parse
 * and only the `noData` split depends on. Sorting and counting run this per row.
 */
function resolveDisplay(
  summary: StatusSummaryLike | null | undefined,
): Pick<StatusView, "status" | "display" | "stale"> | undefined {
  if (!summary || !isAssetStatus(summary.status)) return undefined;
  const status = summary.status;
  const stale = isStale(summary);
  // Offline: it did report, and it has stopped. A recent uplink without an operating state
  // (not commissioned, unknown) is not offline — the device talks, the portal has no verdict.
  const display: AssetDisplayStatus =
    status === "noData" ? (stale && lastSeenAt(summary) ? "offline" : "noData") : status;
  // Nothing delivered yet is "no data", not "old data"; maintenance and decommissioned sleep on
  // purpose (the server never flags them either).
  const staleShown = stale && display !== "noData" && status !== "decommissioned" && status !== "maintenance";
  return { status, display, stale: staleShown };
}

/** `undefined` for a missing summary or an unknown status. */
export function describeStatus(summary: StatusSummaryLike | null | undefined): StatusView | undefined {
  const view = resolveDisplay(summary);
  return view && { ...view, lastSeenAt: lastSeenAt(summary as StatusSummaryLike) };
}

/**
 * Severity order for the equipment table: critical > warning > offline or stale > maintenance
 * > no data > normal > decommissioned; devices without a status last but one. Inside the
 * offline/stale group: stale critical, stale warning, offline, stale normal.
 */
export function severityRank(summary: StatusSummaryLike | null | undefined): number {
  const view = resolveDisplay(summary);
  if (!view) return 6.5;
  if (view.display === "critical" && !view.stale) return 0;
  if (view.display === "warning" && !view.stale) return 1;
  // Within "offline or stale", an old alarm comes first: it may still be real.
  if (view.stale && view.display === "critical") return 2;
  if (view.stale && view.display === "warning") return 2.1;
  if (view.display === "offline") return 2.2;
  if (view.stale) return 2.3;
  switch (view.display) {
    case "maintenance":
      return 3;
    case "noData":
      return 4;
    case "normal":
      return 5;
    default:
      return 7;
  }
}

/** Filter chips: one per display status, plus `stale` across all of them. */
export type StatusFilter = "all" | AssetDisplayStatus | "stale";

export function matchesStatusFilter(summary: StatusSummaryLike | null | undefined, filter: StatusFilter): boolean {
  if (filter === "all") return true;
  const view = resolveDisplay(summary);
  if (!view) return false;
  if (filter === "stale") return view.stale;
  return view.display === filter;
}

export type StatusCounts = Record<AssetDisplayStatus | "stale" | "all", number>;

/** Counts per display status (a partition), `stale` across them, `all` = every id given. */
export function countStatuses(
  ids: string[],
  summaries: Record<string, StatusSummaryLike | null | undefined>,
): StatusCounts {
  const counts = Object.fromEntries(
    [...ASSET_DISPLAY_STATUSES, "stale", "all"].map((key) => [key, 0]),
  ) as StatusCounts;
  for (const id of ids) {
    counts.all += 1;
    const view = resolveDisplay(summaries[id]);
    if (!view) continue;
    counts[view.display] += 1;
    if (view.stale) counts.stale += 1;
  }
  return counts;
}

/** Badge colours per display status; offline shares the grey of no data. */
export const DISPLAY_STATUS_BADGE_CLASSES: Record<AssetDisplayStatus, string> = {
  ...ASSET_STATUS_BADGE_CLASSES,
  offline: "border-transparent bg-muted text-muted-foreground",
};

export const DISPLAY_STATUS_MARKER_COLORS: Record<AssetDisplayStatus, string> = {
  ...ASSET_STATUS_MARKER_COLORS,
  offline: ASSET_STATUS_MARKER_COLORS.noData,
};

/**
 * The metric that tripped a critical/warning, from the reason key the server writes
 * (`lowPressure` → `pressure`, `highBatteryVoltage` → `batteryVoltage`).
 */
export function triggerMetric(reason: string | null | undefined): string | undefined {
  const match = /^(low|high)([A-Z].*)$/.exec(reason ?? "");
  if (!match) return undefined;
  return match[2].charAt(0).toLowerCase() + match[2].slice(1);
}

// ---------------------------------------------------------------------------
// Floor plan markers — colour plus a shape cue, never colour alone
// ---------------------------------------------------------------------------

/** Cue drawn in a small dot on the marker's corner. */
export type MarkerCue =
  | "critical"
  | "warning"
  | "stale"
  | "maintenance"
  | "normal"
  | "decommissioned"
  | "noData";

export interface MarkerStyle {
  color: string;
  cue: MarkerCue;
  /** Ring dashed: the data is not current (stale, offline, no data). */
  dashed: boolean;
  /** Drawn faint: outside the active status filter. */
  dimmed: boolean;
  /** Status not known yet (loading or failed): muted colour, no cue, no alarm semantics. */
  neutral?: boolean;
}

/**
 * Muted slate for the box and glyph of markers whose status is still loading. Clearly lighter
 * than the "unknown/offline" grey (#6b7280), and the neutral look also differs by shape: thin
 * stroke, no cue dot.
 */
export const NEUTRAL_MARKER_COLOR = "#94a3b8";

/** Label colour of a neutral marker: dark, so the name stays readable (~10:1 on white). */
export const NEUTRAL_MARKER_LABEL_COLOR = "#374151";

/** Marker look while the status of a device is not known (loading or request failed). */
export const NEUTRAL_MARKER_STYLE: MarkerStyle = {
  color: NEUTRAL_MARKER_COLOR,
  cue: "noData",
  dashed: false,
  dimmed: false,
  neutral: true,
};

/** What the plan draws for one marker, derived from its style (pure, so it can be unit-tested). */
export interface MarkerPaint {
  boxColor: string;
  labelColor: string;
  glyphColor: string;
  strokeWidth: number;
  dashArray: number[];
  showCue: boolean;
  cue?: MarkerCue;
  opacity: number;
}

/**
 * Paint for a marker. Without a style the marker keeps its own category colour; a neutral style
 * greys only box and glyph (thin stroke, no cue) and keeps the label readable; a status style
 * colours everything with a strong ring and a cue dot.
 */
export function markerPaint(style: MarkerStyle | undefined, baseColor: string): MarkerPaint {
  if (!style) {
    return {
      boxColor: baseColor,
      labelColor: baseColor,
      glyphColor: baseColor,
      strokeWidth: 1,
      dashArray: [],
      showCue: false,
      opacity: 1,
    };
  }
  const neutral = !!style.neutral;
  return {
    boxColor: style.color,
    labelColor: neutral ? NEUTRAL_MARKER_LABEL_COLOR : style.color,
    glyphColor: style.color,
    strokeWidth: neutral ? 1 : 3,
    dashArray: style.dashed ? [4, 3] : [],
    showCue: !neutral,
    cue: neutral ? undefined : style.cue,
    opacity: style.dimmed ? 0.2 : 1,
  };
}

/** Material Symbols glyph per cue (the plan renders them like the category icons). */
export const MARKER_CUE_GLYPHS: Record<MarkerCue, string> = {
  critical: "priority_high",
  warning: "warning",
  stale: "schedule",
  maintenance: "build",
  normal: "check",
  decommissioned: "block",
  noData: "remove",
};

/** Plain-text stand-in while the icon font is not loaded. */
export const MARKER_CUE_FALLBACK: Record<MarkerCue, string> = {
  critical: "!",
  warning: "!",
  stale: "~",
  maintenance: "W",
  normal: "✓",
  decommissioned: "×",
  noData: "–",
};

/** Marker style for one device; `undefined` without a usable status (marker keeps its own look). */
export function markerStyleForSummary(
  summary: StatusSummaryLike | null | undefined,
  filter: StatusFilter = "all",
): MarkerStyle | undefined {
  const view = describeStatus(summary);
  if (!view) return undefined;
  // Critical and warning keep their cue when old — they stay actionable; the dashed ring says
  // the data behind them is old.
  const cue: MarkerCue =
    view.display === "critical"
      ? "critical"
      : view.display === "warning"
        ? "warning"
        : view.display === "offline" || view.stale
          ? "stale"
          : view.display;
  return {
    color: DISPLAY_STATUS_MARKER_COLORS[view.display],
    cue,
    dashed: view.stale || view.display === "noData",
    dimmed: !matchesStatusFilter(summary, filter),
  };
}

export function markerStylesFromSummaries(
  summaries: Record<string, StatusSummaryLike | null | undefined>,
  filter: StatusFilter = "all",
): Record<string, MarkerStyle> {
  const styles: Record<string, MarkerStyle> = {};
  for (const [assetId, summary] of Object.entries(summaries)) {
    const style = markerStyleForSummary(summary, filter);
    if (style) styles[assetId] = style;
  }
  return styles;
}

/**
 * Marker styles for the plan. `pendingIds` are the ids with no settled result (still loading,
 * or the request failed); those without a summary get the neutral look, so "not loaded yet"
 * never reads as the red/unknown status. Ids the server left out of a settled response are not
 * pending and keep the marker's own look. With an active status filter a neutral marker is
 * faint, like any marker outside the filter.
 */
export function markerStylesWithPending(
  assetIds: string[],
  summaries: Record<string, StatusSummaryLike | null | undefined>,
  filter: StatusFilter,
  pendingIds: Iterable<string>,
): Record<string, MarkerStyle> {
  const styles = markerStylesFromSummaries(summaries, filter);
  const wanted = new Set(assetIds);
  const neutral: MarkerStyle = { ...NEUTRAL_MARKER_STYLE, dimmed: filter !== "all" };
  for (const id of Array.from(pendingIds)) {
    if (wanted.has(id) && !styles[id] && !summaries[id]) styles[id] = neutral;
  }
  return styles;
}
